import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { useSnackbar } from 'notistack'
import { get } from '../../common/constants'
import { formatDate1 } from '../../common/util'
import { kpiCardSx, kpiRowSx, chartContainerSx, chartSeriesColors, tableHeadSx } from '../../common/sharedStyles'

import { FilterCard, FilterCardContent, FilterRow, FieldLabel, FilterField, FilterDateField, FilterActions, ApplyButton, ResetButton } from '../../common/FilterBar'
import { COURIER_LIST_QUERY_PAYLOAD } from '../../common/ProshipFormComponents'
import { useGetAllActiveMerchantsIdAndNameCachedQuery, useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import DateRangePicker from '../../components/DateRangePicker'
import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'

/**
 * RVP QC Monitor — did every QC-flagged reverse pickup get a doorstep QC?
 * Rows are registration-day cohorts (order date): QC-flagged reverse orders created that day, how many have
 * been picked up so far, and how many of those pickups came back with QC images from the courier.
 * Backend: GET admin/qc-monitor/summary and admin/qc-monitor/missing (ROLE_PROSHIPADMIN).
 * Default window is today. Recent cohorts are still filling in (pickups happen 0-3 days after registration).
 */

const MAX_DAYS = 31
// Series colours for the trend: the shared list minus yellow (#FFE700 is unreadable on white).
const TREND_COLORS = chartSeriesColors.filter((c) => c.toLowerCase() !== '#ffe700')
const API_FMT = 'yyyy-MM-dd'
const ALL = '__ALL__'

// Coverage bands (% of QC pickups with images). Healthy couriers run 95%+ in prod.
const BAND = {
    good: { bg: '#e7f4ea', ink: '#1e6b34' },
    warn: { bg: '#fdf3dd', ink: '#8a5d07' },
    crit: { bg: '#fdeae8', ink: '#a03024' },
}
const bandOf = (pct) => (pct == null ? 'good' : pct >= 95 ? 'good' : pct >= 85 ? 'warn' : 'crit')

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
const daysAgo = (n) => { const d = startOfDay(new Date()); d.setDate(d.getDate() - n); return d }
const dayDiff = (a, b) => Math.round((startOfDay(b) - startOfDay(a)) / 86400000)

const CHIPS = [
    { key: 'today', label: 'Today', range: () => ({ from: daysAgo(0), to: daysAgo(0) }) },
    { key: 'yesterday', label: 'Yesterday', range: () => ({ from: daysAgo(1), to: daysAgo(1) }) },
    { key: '7d', label: 'Last 7 days', range: () => ({ from: daysAgo(6), to: daysAgo(0) }) },
    { key: '30d', label: 'Last 30 days', range: () => ({ from: daysAgo(29), to: daysAgo(0) }) },
]

const fmtNum = (v) => (v == null ? '—' : Math.round(v).toLocaleString('en-IN'))
const fmtPct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`)
const pct = (num, den) => (den ? (num * 100) / den : null)
const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—')
const ageLabel = (iso) => {
    if (!iso) return '—'
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
    const h = Math.floor(mins / 60), m = mins % 60
    return h ? `${h}h ${m}m` : `${m}m`
}

// Client-side CSV of every row the API returned (the grid only paginates the view).
const csvEscape = (v) => {
    const str = v == null ? '' : String(v)
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}
const downloadCsv = (filename, header, rows) => {
    const lines = [header.map(csvEscape).join(','), ...rows.map((r) => r.map(csvEscape).join(','))]
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

const rangeLabel = (r) =>
    dayDiff(r.from, r.to) === 0
        ? formatDate1(r.from, 'dd MMM yyyy')
        : `${formatDate1(r.from, 'dd MMM')} – ${formatDate1(r.to, 'dd MMM yyyy')}`

export default function QcMonitor() {
    const { enqueueSnackbar } = useSnackbar()

    // Courier options come from the cached courier master (same source as the shared FilterBar),
    // so the dropdown is populated even when the current window has no QC orders.
    const { data: courierMaster = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_QUERY_PAYLOAD)
    const courierOptions = useMemo(
        () => Array.from(new Set((courierMaster || []).map((c) => c?.parent).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
        [courierMaster]
    )

    // Merchant list from the shared cached query (same source as the FilterBar merchant field).
    const { data: merchantMaster = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
    const merchantOptions = useMemo(() => (merchantMaster || [])
        .filter((m) => m && m.is_deleted === '0')
        .map((m) => ({ value: m.id, label: m.companyName ? `${m.name} (${m.companyName})` : m.name }))
        .sort((a, b) => a.label.localeCompare(b.label)), [merchantMaster])
    const ALL_MERCHANTS = useMemo(() => ({ value: ALL, label: 'All Merchants' }), [])

    // Applied filters drive the API calls; pending values are what the user is editing until Filter is clicked.
    const [chip, setChip] = useState('today')
    const [range, setRange] = useState(() => CHIPS[0].range())
    const [courier, setCourier] = useState(ALL)
    const [merchant, setMerchant] = useState(ALL_MERCHANTS)
    const [pendingRange, setPendingRange] = useState(null)
    const [pendingCourier, setPendingCourier] = useState(ALL)
    const [pendingMerchant, setPendingMerchant] = useState(ALL_MERCHANTS)
    const [summary, setSummary] = useState(null)
    const [missing, setMissing] = useState(null)
    const [loadingSummary, setLoadingSummary] = useState(false)
    const [loadingMissing, setLoadingMissing] = useState(false)
    const [page, setPage] = useState(0)
    const [sizePerPage, setSizePerPage] = useState(20)

    // Quick ranges apply immediately (they are shortcuts, like the latency page).
    const applyChip = (c) => { setChip(c.key); setRange(c.range()); setPendingRange(null) }

    // Custom range + courier are applied together on Filter, like the shared FilterBar.
    const pickCustom = (r) => {
        if (!r || !r.length || !r[0].startDate || !r[0].endDate) return
        setPendingRange({ from: startOfDay(r[0].startDate), to: startOfDay(r[0].endDate) })
    }
    const applyFilters = () => {
        if (pendingRange) {
            if (dayDiff(pendingRange.from, pendingRange.to) >= MAX_DAYS) {
                enqueueSnackbar(`Pick at most ${MAX_DAYS} days.`, { variant: 'warning' })
                return
            }
            setChip('custom')
            setRange(pendingRange)
        }
        // Backend matches courier parent case-insensitively; summary rows carry it lower-cased.
        setCourier(pendingCourier === ALL ? ALL : pendingCourier.toLowerCase())
        setMerchant(pendingMerchant || ALL_MERCHANTS)
    }
    const resetFilters = () => {
        setPendingRange(null); setPendingCourier(ALL); setPendingMerchant(ALL_MERCHANTS)
        setChip('today'); setRange(CHIPS[0].range()); setCourier(ALL); setMerchant(ALL_MERCHANTS)
    }

    // Merchant is a server-side filter (the summary is grouped in Mongo); courier is client-side on the
    // summary and server-side on the missing list.
    const params = useMemo(() => ({
        from: formatDate1(range.from, API_FMT), to: formatDate1(range.to, API_FMT),
        ...(merchant.value !== ALL ? { merchantId: merchant.value } : {}),
    }), [range, merchant])

    const loadSummary = useCallback(() => {
        setLoadingSummary(true)
        get('admin/qc-monitor/summary', params)
            .then((res) => setSummary(res.data?.responseObj || null))
            .catch(() => enqueueSnackbar('Failed to load QC summary', { variant: 'error' }))
            .finally(() => setLoadingSummary(false))
    }, [params, enqueueSnackbar])

    const loadMissing = useCallback(() => {
        setLoadingMissing(true)
        const p = courier === ALL ? params : { ...params, courier }
        get('admin/qc-monitor/missing', p)
            .then((res) => { setMissing(res.data?.responseObj || null); setPage(0) })
            .catch(() => enqueueSnackbar('Failed to load missing AWBs', { variant: 'error' }))
            .finally(() => setLoadingMissing(false))
    }, [params, courier, enqueueSnackbar])

    useEffect(() => { loadSummary() }, [loadSummary])
    useEffect(() => { loadMissing() }, [loadMissing])

    // ── derived ──────────────────────────────────────────────────────────────
    const rows = useMemo(() => (summary?.rows || []).filter((r) => courier === ALL || r.courier === courier), [summary, courier])
    // Coverage numerator is pickedUpWithImages (picked up AND images). withImages alone also counts
    // QC-failed attempts that ended cancelled, so it is not used for coverage.
    const totals = useMemo(() => rows.reduce((t, r) => ({
        created: t.created + r.created, pickedUp: t.pickedUp + r.pickedUp,
        pickedUpWithImages: t.pickedUpWithImages + (r.pickedUpWithImages ?? 0), missing: t.missing + r.missing,
    }), { created: 0, pickedUp: 0, pickedUpWithImages: 0, missing: 0 }), [rows])

    const byCourier = useMemo(() => {
        const m = new Map()
        rows.forEach((r) => {
            const c = m.get(r.courier) || { courier: r.courier, created: 0, pickedUp: 0, pickedUpWithImages: 0, missing: 0 }
            c.created += r.created; c.pickedUp += r.pickedUp; c.pickedUpWithImages += (r.pickedUpWithImages ?? 0); c.missing += r.missing
            m.set(r.courier, c)
        })
        return Array.from(m.values())
            .map((c) => ({ ...c, coverage: pct(c.pickedUpWithImages, c.pickedUp) }))
            .sort((a, b) => (a.coverage ?? 101) - (b.coverage ?? 101))
    }, [rows])

    const multiDay = dayDiff(range.from, range.to) > 0
    const trend = useMemo(() => {
        if (!multiDay) return { data: [], series: [] }
        const days = Array.from(new Set(rows.map((r) => r.day))).sort()
        // Only couriers with at least one pickup in the window; a courier with no pickups has no coverage to plot.
        const pickedByCourier = rows.reduce((m, r) => { m[r.courier] = (m[r.courier] || 0) + r.pickedUp; return m }, {})
        const series = Object.keys(pickedByCourier).filter((c) => pickedByCourier[c] > 0).sort()
        const data = days.map((day) => {
            const point = { day: formatDate1(day, 'dd MMM') }
            series.forEach((c) => {
                const r = rows.find((x) => x.day === day && x.courier === c)
                point[c] = r ? pct(r.pickedUpWithImages ?? 0, r.pickedUp) : null
            })
            return point
        })
        return { data, series }
    }, [rows, multiDay])

    const missingRows = useMemo(() => (missing?.items || []).map((it, i) => ({ id: `${it.awbNumber}-${i}`, ...it })), [missing])

    const columns = useMemo(() => [
        { field: 'awbNumber', headerName: 'AWB Number', flex: 1.2, minWidth: 160 },
        { field: 'orderDate', headerName: 'Order Date', flex: 0.7, minWidth: 110 },
        { field: 'courier', headerName: 'Courier', flex: 0.8, minWidth: 120 },
        { field: 'courierAccount', headerName: 'Account', flex: 1.4, minWidth: 200 },
        { field: 'merchantName', headerName: 'Merchant Name', flex: 1.2, minWidth: 160 },
        { field: 'pickedUpAt', headerName: 'Pickup Date', flex: 1, minWidth: 150, valueFormatter: ({ value }) => (value ? formatDate1(value) : '—') },
        { field: 'age', headerName: 'Since Pickup', flex: 0.7, minWidth: 110, sortable: false, valueGetter: ({ row }) => ageLabel(row.pickedUpAt) },
    ], [])

    const coverage = pct(totals.pickedUpWithImages, totals.pickedUp)

    const downloadMissingCsv = () => {
        const header = ['AWB Number', 'Order Date', 'Courier', 'Account', 'Merchant Name', 'Pickup Date', 'Since Pickup']
        const body = missingRows.map((r) => [
            r.awbNumber, r.orderDate, r.courier, r.courierAccount, r.merchantName,
            r.pickedUpAt ? formatDate1(r.pickedUpAt) : '', ageLabel(r.pickedUpAt),
        ])
        const slug = (v) => String(v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
        const scope = [courier !== ALL ? slug(courier) : '', merchant.value !== ALL ? slug(merchant.label) : ''].filter(Boolean).map((x) => `-${x}`).join('')
        downloadCsv(`qc-missing-images-${params.from}-to-${params.to}${scope}.csv`, header, body)
    }

    return (
        <>
            <PageTitle>RVP QC Monitor</PageTitle>
            <Page title="">
                {/* ── filters (shared FilterBar look: label-above fields, Filter / Reset) ── */}
                <FilterCard>
                    <FilterCardContent>
                        <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1.5 }}>
                            <FieldLabel sx={{ mb: 0, mr: 0.5 }}>Quick range</FieldLabel>
                            {CHIPS.map((c) => (
                                <Chip key={c.key} label={c.label} size="small" clickable
                                    color={chip === c.key ? 'primary' : 'default'}
                                    variant={chip === c.key ? 'filled' : 'outlined'}
                                    onClick={() => applyChip(c)} />
                            ))}
                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                Showing <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{rangeLabel(range)}</Box>
                                {courier !== ALL && <> · <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{titleCase(courier)}</Box></>}
                                {merchant.value !== ALL && <> · <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{merchant.label}</Box></>}
                            </Typography>
                        </Stack>
                        <FilterRow>
                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <Autocomplete
                                    size="small"
                                    fullWidth
                                    disableClearable
                                    options={[ALL_MERCHANTS, ...merchantOptions]}
                                    value={pendingMerchant}
                                    isOptionEqualToValue={(o, v) => o.value === v.value}
                                    getOptionLabel={(o) => o.label}
                                    onChange={(_, v) => setPendingMerchant(v || ALL_MERCHANTS)}
                                    renderInput={(p) => <TextField {...p} placeholder={merchantOptions.length ? 'Select Merchant' : 'Loading merchants…'} />}
                                />
                            </FilterField>
                            <FilterField>
                                <FieldLabel>Courier</FieldLabel>
                                <Autocomplete
                                    size="small"
                                    fullWidth
                                    disableClearable
                                    options={[ALL, ...courierOptions]}
                                    value={pendingCourier}
                                    getOptionLabel={(o) => (o === ALL ? 'All Couriers' : o)}
                                    onChange={(_, v) => setPendingCourier(v || ALL)}
                                    renderInput={(p) => <TextField {...p} placeholder={courierOptions.length ? 'Select Courier' : 'Loading couriers…'} />}
                                />
                            </FilterField>
                            <FilterDateField>
                                <FieldLabel>Order Date Range</FieldLabel>
                                <DateRangePicker
                                    onChange={pickCustom}
                                    hideOkButton
                                    size="small"
                                    defaultRange={pendingRange ? [{ startDate: pendingRange.from, endDate: pendingRange.to, key: 'selection' }] : []}
                                />
                            </FilterDateField>
                            <FilterActions>
                                <ApplyButton variant="contained" size="small" onClick={applyFilters} disabled={loadingSummary || loadingMissing}>Filter</ApplyButton>
                                <ResetButton variant="contained" size="small" onClick={resetFilters}>Reset</ResetButton>
                            </FilterActions>
                        </FilterRow>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.25 }}>
                            Doorstep-QC reverse orders with an AWB, by order date. Coverage = picked up with QC images ÷ picked up. The newest days are still being picked up.
                        </Typography>
                    </FilterCardContent>
                </FilterCard>

                {/* ── KPIs ── */}
                <Box sx={kpiRowSx}>
                    <Kpi label="QC orders manifested" value={fmtNum(totals.created)} token="primary" loading={loadingSummary} />
                    <Kpi label="Picked up" value={fmtNum(totals.pickedUp)} token="info" loading={loadingSummary} />
                    <Kpi label="Picked up with QC images" value={fmtNum(totals.pickedUpWithImages)} token="success" loading={loadingSummary} />
                    <Kpi label="Missing Images" value={fmtNum(totals.missing)} token="error" loading={loadingSummary} />
                    <Kpi label="Coverage" value={fmtPct(coverage)} token={bandOf(coverage) === 'crit' ? 'error' : bandOf(coverage) === 'warn' ? 'warning' : 'success'} loading={loadingSummary} />
                </Box>

                {/* ── per courier ── */}
                <Card sx={{ mb: 2.5 }}>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Coverage by courier</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead sx={tableHeadSx}>
                                    <TableRow>
                                        <TableCell>Courier</TableCell>
                                        <TableCell align="right">Manifested</TableCell>
                                        <TableCell align="right">Picked up</TableCell>
                                        <TableCell align="right">With QC Images</TableCell>
                                        <TableCell align="right">Missing Images</TableCell>
                                        <TableCell align="right">Coverage</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {byCourier.length === 0 && (
                                        <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                                            {loadingSummary ? <CircularProgress size={20} /> : 'No QC-flagged reverse orders registered in this range.'}
                                        </TableCell></TableRow>
                                    )}
                                    {byCourier.map((c) => {
                                        const b = BAND[bandOf(c.coverage)]
                                        return (
                                            <TableRow key={c.courier} hover>
                                                <TableCell sx={{ fontWeight: 600 }}>{titleCase(c.courier)}</TableCell>
                                                <TableCell align="right">{fmtNum(c.created)}</TableCell>
                                                <TableCell align="right">{fmtNum(c.pickedUp)}</TableCell>
                                                <TableCell align="right">{fmtNum(c.pickedUpWithImages)}</TableCell>
                                                <TableCell align="right" sx={{ color: c.missing ? BAND.crit.ink : 'inherit', fontWeight: c.missing ? 700 : 400 }}>{fmtNum(c.missing)}</TableCell>
                                                <TableCell align="right">
                                                    <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, bgcolor: b.bg, color: b.ink }}>
                                                        {fmtPct(c.coverage)}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                {/* ── trend (multi-day only) ── */}
                {multiDay && trend.data.length > 0 && (
                    <Box sx={chartContainerSx}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Coverage % by order date</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                            Share of each day's picked-up QC orders that came back with QC images. A point below the band means the courier skipped QC on that day's orders.
                        </Typography>
                        <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={trend.data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                                <YAxis domain={[0, 100]} allowDataOverflow ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} width={44} />
                                <Tooltip formatter={(v) => (v == null ? '—' : `${v.toFixed(1)}%`)} />
                                <Legend formatter={(v) => titleCase(v)} />
                                {trend.series.map((s, i) => (
                                    <Line key={s} type="linear" dataKey={s} stroke={TREND_COLORS[i % TREND_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                )}

                {/* ── missing AWBs ── */}
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Picked up without QC images</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {missing ? `${fmtNum(missing.count)} AWB${missing.count === 1 ? '' : 's'}${missing.truncated ? ' (first 2,000 only — narrow the range or courier)' : ''}` : ''}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />}
                            disabled={loadingMissing || missingRows.length === 0}
                            onClick={downloadMissingCsv}>
                            Download CSV{missingRows.length ? ` (${fmtNum(missingRows.length)})` : ''}
                        </Button>
                    </Stack>
                    {loadingMissing ? (
                        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
                    ) : (
                        <ProzoDataGrid
                            rows={missingRows}
                            columns={columns}
                            paginationMode="client"
                            rowCount={missingRows.length}
                            page={page}
                            setPage={setPage}
                            sizePerPage={sizePerPage}
                            setSizePerPage={setSizePerPage}
                            getRowId={(r) => r.id}
                            exportFileName={`qc-missing-${params.from}-${params.to}`}
                            disableColumnMenu
                        />
                    )}
                </Paper>
            </Page>
        </>
    )
}

function Kpi({ label, value, token, loading }) {
    return (
        <Paper sx={kpiCardSx(token)}>
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>{label}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, opacity: loading ? 0.4 : 1 }}>{loading ? '—' : value}</Typography>
        </Paper>
    )
}
